/*
 * Copyright © 2022-2024 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState, useEffect } from 'react';
import ChipInput from 'material-ui-chip-input';
import { Trans, useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Paper, Typography, InputAdornment, IconButton } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import AssistiveText from './AssistiveText';
import { text } from '../../../shared/validations/standardChecks';
import Tooltip from '../../common/Tooltip';
import AttachmentUploader from '../../draft/attachments/uploader/AttachmentUploader';

const useStyles = makeStyles(theme => ({
  chipInput: ({ disabled }) => {
    let styles = {
      '& .MuiChip-clickable': {
        overflow: 'auto',
      },
      '& .MuiInputBase-root': {
        paddingRight: '40px', // Reserve enough space for the clear button
      },
      '& .MuiInputBase-root > div': {
        display: 'flex',
        flexWrap: 'wrap', // Only wrap when absolutely necessary
      },
      // Give the input a proper minimum width
      '& input:focus': {
        minWidth: '8ch !important', // Reserve space for a few characters
      },
    };
    if (disabled) {
      styles = {
        ...styles,
        '& .MuiChip-root': {
          zIndex: 1,
          border: `1px solid ${theme.palette.common.black}`,
        },
        '& .MuiChip-deleteIcon': {
          display: 'none',
        },
      };
    }
    return styles;
  },
  ipcCodes: {
    [theme.breakpoints.up('sm')]: {
      width: '95%',
    },
    [theme.breakpoints.up('md')]: {
      width: 'inherit',
    },
  },
  paper: {
    margin: '1.5rem 0',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.elementBackground,
    boxShadow: 'none',
    borderRadius: '1rem',
  },
  titleText: {
    color: theme.palette.titleText,
    paddingBottom: '2rem',
    textAlign: 'center',
  },
  clearButton: {
    marginTop: '0.5rem',
    marginLeft: '0.5rem',
  },
  chipInputContainer: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    columnGap: '8px',
  },
  clearIconButton: {
    padding: 0,
    margin: 0,
    background: 'none',
    position: 'absolute',
    right: '0.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
    '&:hover': {
      background: 'none',
    },
  },
  clearIcon: {
    fontSize: '1.2rem',
  },
}));

const ChipsField = ({
  formData,
  schema,
  uiSchema,
  disabled,
  readOnly,
  rawErrors,
  label,
  onChange,
  onClear,
  onRemoveItem,
  idSchema,
  registry,
}) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState(null);
  const maxItems = schema.maxItems || Number.MAX_SAFE_INTEGER;
  const classes = useStyles({ disabled });
  const isDisabled = uiSchema?.['ui:disabled'] || disabled;
  const displayLabel = uiSchema?.[uiSchemaProperties.UI_TITLE]
    ? (uiSchema?.title && t(uiSchema?.config?.title)) || label || schema.title
    : false;

  const [ipcCodes, setIpcCodes] = useState(formData || []);
  const tooltip = schema?.config?.tooltip ?? uiSchema?.[uiSchemaProperties.UI_TOOLTIP];
  const separator = uiSchema?.[uiSchemaProperties.UI_SEPARATOR];
  const fileImport = uiSchema?.[uiSchemaProperties.UI_FILE_IMPORT];

  const shouldTriggerPostProcess = !!uiSchema?.[uiSchemaProperties.UI_OPTIONS]?.triggerPostProcess;

  // Allow invalid input based on provided pattern to be added as Chip
  const allowInvalidInput = uiSchema?.['ui:allowInvalidInput'] || false;

  const handleAddChip = (chip) => {
    if (allowInvalidInput && ipcCodes?.length > 0) {
      const allChipsValid = checkAllChipsMatchPattern(ipcCodes);
      if (allChipsValid) {
        setErrorMessage(null);
      }
    } else {
      setErrorMessage(null);
    }

    const lengthValidation = text(0, schema?.items?.maxLength)(chip);
    if (!lengthValidation) {
      setErrorMessage(lengthValidation);
      return;
    }

    const chipArr = separator ? chip?.split(separator) : [chip];
    chipArr.forEach((_chip) => {
      const patternValidation = schema?.items?.pattern != null ? new RegExp(schema?.items?.pattern).test(_chip) : false;
      if (!patternValidation) {
        setErrorMessage(t(uiSchema?.['ui:validationPatternMessage']));
        if (!allowInvalidInput) {
          return;
        }
      }
      if (ipcCodes.length < maxItems) {
        ipcCodes.push(_chip?.trim());
      } else {
        setErrorMessage(
          errorMessage
            ? `${t(uiSchema?.['ui:validationmaxlength'])} ${t(uiSchema?.['ui:validationPatternMessage'])}`
            : t(uiSchema?.['ui:validationmaxlength']),
        );
      }
    });
    onChange(ipcCodes);
    setIpcCodes(ipcCodes);
    if (shouldTriggerPostProcess) {
      registry.triggerPostProcess(`${idSchema.$id}`, ipcCodes, false);
    }
  };

  const handleDeleteChip = (chip, index) => {
    if (shouldTriggerPostProcess) {
      registry.triggerPostProcess(`${idSchema.$id}`, ipcCodes, false);
    }

    const newIpcCodes = ipcCodes.slice();
    newIpcCodes.splice(index, 1);
    setIpcCodes(newIpcCodes);
    onChange(newIpcCodes);

    // Remove specific search result when a chip is deleted
    if (typeof onRemoveItem === 'function') {
      onRemoveItem(chip);
    }

    if (allowInvalidInput) {
      const allChipsValid = checkAllChipsMatchPattern(newIpcCodes);
      if (allChipsValid) {
        setErrorMessage(null);
      }
    }
  };

  // Prevent adding more than maxLenght chars in chimpInput
  const handleKeyDown = (e) => {
    if (
      uiSchema?.[uiSchemaProperties.UI_VALIDATION]?.maxLength &&
      e.target.value.length >= schema?.items?.maxLength &&
      e.key !== 'Backspace' &&
      e.key !== 'Enter'
    ) {
      e.preventDefault();
    }
  };

  // Check if all provided patent numbers are valid
  const checkAllChipsMatchPattern = (chips) => {
    const pattern = schema?.items?.pattern ? new RegExp(schema.items.pattern) : null;
    if (pattern) {
      const allCodesMatchPattern = chips.every(item => pattern.test(item));
      if (allCodesMatchPattern) {
        return true;
      }
    }
    return false;
  };

  // Trigger an error if any provided patent number is invalid
  useEffect(() => {
    if (allowInvalidInput && ipcCodes?.length > 0) {
      const allChipsValid = checkAllChipsMatchPattern(ipcCodes);
      if (!allChipsValid) {
        setErrorMessage(t(uiSchema?.['ui:validationPatternMessage']));
      }
    }
  }, []);

  const handleCsvImport = async (droppedFiles, onRemoveDroppedFile) => {
    const csvContent = await droppedFiles[0].text();
    const importedCodes = csvContent.split(new RegExp(`[\r\n]+|${fileImport.separator}`, 'g'));

    let patternValidation = null;
    if (schema?.items?.pattern != null) {
      patternValidation = new RegExp(schema?.items?.pattern);
    }
    importedCodes.forEach((code) => {
      if (patternValidation == null || patternValidation.test(code)) {
        ipcCodes.push(code);
      }
    });

    const newIpcCodes = [...new Set(ipcCodes)];
    setIpcCodes(newIpcCodes);
    onChange(newIpcCodes);
    onRemoveDroppedFile(droppedFiles[0].name);
    if (shouldTriggerPostProcess) {
      registry.triggerPostProcess(`${idSchema.$id}`, ipcCodes, false);
    }
  };

  const handleClearAll = () => {
    setIpcCodes([]);
    onChange([]);
    setErrorMessage(null);
    // Clear all search results when all chips are cleared
    if (typeof onClear === 'function') {
      onClear();
    }
  };

  const ChipComponent = () => (
    <div className={classes.ipcCodes}>
      <div>
        <Typography variant="subtitle1" component="p">
          <p>
            <Trans i18nKey={uiSchema?.[uiSchemaProperties.TITLE_FIELD]} />
          </p>
        </Typography>
        <div className={classes.chipInputContainer}>
          <ChipInput
            className={classes.chipInput}
            data-testid="chip-input"
            blurBehavior="add"
            value={ipcCodes}
            onAdd={chip => handleAddChip(chip)}
            onDelete={(chip, index) => handleDeleteChip(chip, index)}
            disabled={isDisabled}
            readOnly={readOnly}
            error={rawErrors.length > 0}
            label={displayLabel}
            variant="outlined"
            fullWidth
            fullWidthInput
            onKeyDown={handleKeyDown}
            inputRef={(ref) => {
              if (ref) {
                ref.setAttribute('aria-label', displayLabel);
              }
            }}
            InputProps={{
              notched: true,
              label: displayLabel,
              endAdornment: ipcCodes.length > 0 && ( // Conditionally render the button
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear all chips"
                    onClick={handleClearAll}
                    disabled={isDisabled || readOnly}
                    edge="end"
                    className={classes.clearIconButton}
                  >
                    <ClearIcon className={classes.clearIcon} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {tooltip && <Tooltip className={classes.tooltip} title={<Trans i18nKey={tooltip} />} />}
        </div>
      </div>
      {errorMessage && <AssistiveText severity="error">{errorMessage}</AssistiveText>}
    </div>
  );

  const attachmentCategory = fileImport?.[uiSchemaProperties.ATTACHMENTS_CATEGORIES]?.[0] || 'CSV';

  if (!fileImport) {
    return <ChipComponent />;
  }
  return (
    <Paper className={classes.paper}>
      {!disabled && <Typography className={classes.titleText}>{fileImport[uiSchemaProperties.UI_TITLE]}</Typography>}
      <ChipComponent />
      <AttachmentUploader
        attachmentCategory={attachmentCategory}
        attachmentType={fileImport?.[uiSchemaProperties.ATTACHMENTS_CATEGORIES][0]}
        metaFieldNames={[]}
        disabledAttachmentTypes={[]}
        attachmentsMetadata={[]}
        mergedAttachmentsMetadata={[]}
        onFileDropped={() => {}}
        onDroppedFileRemoved={() => {}}
        onAttachmentUploaded={() => {}}
        uiSchema={{}}
        jsonSchema={{}}
        parentClasses={classes}
        readOnly={disabled}
        onConfirmUploadArg={handleCsvImport}
        confirmLabel={fileImport?.[uiSchemaProperties.CONFIRM_BUTTON]}
      />
    </Paper>
  );
};

ChipsField.defaultProps = {
  rawErrors: [],
  uiSchema: {},
  schema: {},
  onClear: () => {},
  onRemoveItem: () => {},
};
export default ChipsField;
